import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Switch } from 'components/ui/switch';
import { useToast } from 'components/ui/use-toast';
import { cn } from 'lib/utils';
import * as Api from '../../api';
import type { ExamConfig, ExamVersionMode } from '../../types';

interface Props {
  testId: string;
  config: ExamConfig;
  locked: boolean;
  usesLibrary: boolean;
  onSaved: () => void;
}

/** Exam-level settings: IELTS variant; SAT routing, break and tools; test versions. */
export function ExamSettingsCard({ testId, config, locked, usesLibrary, onSaved }: Props) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [draft, setDraft] = useState<ExamConfig>(config);
  const [saving, setSaving] = useState(false);
  useEffect(() => setDraft(config), [config]);

  const sat = draft.standard === 'SAT';
  const versions = draft.versions ?? { mode: 'SAME' as ExamVersionMode, whenExhausted: 'REUSE_OLDEST' as const, showVersionCode: false };

  const save = async () => {
    setSaving(true);
    try {
      await Api.UpdateExamSettings(testId, {
        // Only the variant can change after creation: the API rejects a `scope` (a Speaking-only or part test keeps its own).
        ...(sat ? { sat: draft.sat } : { ielts: draft.ielts && { variant: draft.ielts.variant } }),
        versions: draft.versions,
      });
      toast.success(t('saved'));
      onSaved();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('failed_to_save'));
    } finally {
      setSaving(false);
    }
  };

  const setSat = (patch: Partial<NonNullable<ExamConfig['sat']>>) => setDraft((d) => ({ ...d, sat: { ...d.sat!, ...patch } }));

  const versionOptions: Array<{ mode: ExamVersionMode; label: string; help: string }> = [
    { mode: 'SAME', label: t('exam.versions.same'), help: t('exam.versions.same_help') },
    { mode: 'PER_STUDENT', label: t('exam.versions.per_student'), help: t('exam.versions.per_student_help') },
    { mode: 'NO_REPEAT', label: t('exam.versions.no_repeat'), help: t('exam.versions.no_repeat_help') },
  ];

  return (
    <section aria-labelledby="exam-settings-title" className="space-y-5 rounded-xl border border-border bg-card p-4 sm:p-5">
      <h3 id="exam-settings-title" className="font-display text-lg font-bold text-foreground">
        {t('exam.settings.title')}
      </h3>

      {!sat && (
        <fieldset>
          <legend className="mb-2 text-sm font-semibold text-foreground">{t('exam.settings.ielts_variant')}</legend>
          <div className="flex gap-2">
            {(['ACADEMIC', 'GENERAL'] as const).map((v) => (
              <button
                key={v}
                type="button"
                aria-pressed={draft.ielts?.variant === v}
                disabled={locked}
                onClick={() => setDraft((d) => ({ ...d, ielts: { variant: v } }))}
                className={cn(
                  'rounded-md border px-3 py-1.5 text-sm font-medium',
                  draft.ielts?.variant === v ? 'border-foreground bg-foreground text-background' : 'border-border',
                )}
              >
                {v === 'ACADEMIC' ? t('exam.ielts.academic') : t('exam.ielts.general')}
              </button>
            ))}
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground">{t('exam.settings.variant_help')}</p>
        </fieldset>
      )}

      {sat && draft.sat && (
        <>
          <fieldset className="space-y-2">
            <legend className="text-sm font-semibold text-foreground">{t('exam.settings.routing')}</legend>
            <p className="text-xs text-muted-foreground">{t('exam.settings.routing_help')}</p>
            <div className="flex flex-wrap gap-4">
              {(['SAT_RW', 'SAT_MATH'] as const).map((s) => (
                <label key={s} className="space-y-1 text-sm">
                  <span>{t(s === 'SAT_RW' ? 'exam.kind.sat_rw' : 'exam.kind.sat_math')}</span>
                  <Input
                    type="number"
                    min={0}
                    max={200}
                    value={draft.sat!.routing[s]}
                    disabled={locked}
                    onChange={(e) => setSat({ routing: { ...draft.sat!.routing, [s]: Number(e.target.value) } })}
                    className="h-9 w-28"
                  />
                </label>
              ))}
            </div>
          </fieldset>
          <label className="block space-y-1 text-sm">
            <span className="font-semibold text-foreground">{t('exam.settings.break')}</span>
            <Input
              type="number"
              min={0}
              max={30}
              value={Math.round(draft.sat.breakSeconds / 60)}
              disabled={locked}
              onChange={(e) => setSat({ breakSeconds: Math.max(0, Math.min(30, Number(e.target.value))) * 60 })}
              className="h-9 w-28"
            />
            <span className="block text-xs text-muted-foreground">{t('exam.settings.break_help')}</span>
          </label>
          <fieldset className="space-y-2">
            <legend className="text-sm font-semibold text-foreground">{t('exam.settings.tools')}</legend>
            {(['referenceSheet', 'annotator', 'eliminator'] as const).map((tool) => (
              <div key={tool} className="flex items-center justify-between gap-3 text-sm">
                <span>{t(`exam.settings.tool_${tool}`)}</span>
                <Switch
                  checked={draft.sat!.tools[tool]}
                  disabled={locked}
                  onChange={(checked) => setSat({ tools: { ...draft.sat!.tools, [tool]: checked } })}
                />
              </div>
            ))}
          </fieldset>
        </>
      )}

      <fieldset className="space-y-2">
        <legend className="text-sm font-semibold text-foreground">{t('exam.versions.title')}</legend>
        {!usesLibrary && <p className="text-xs text-muted-foreground">{t('exam.versions.needs_library')}</p>}
        <div role="radiogroup" className="grid grid-cols-1 gap-2">
          {versionOptions.map((o) => (
            <button
              key={o.mode}
              type="button"
              role="radio"
              aria-checked={versions.mode === o.mode}
              disabled={locked}
              onClick={() => setDraft((d) => ({ ...d, versions: { ...versions, mode: o.mode } }))}
              className={cn(
                'rounded-lg border p-3 text-left',
                versions.mode === o.mode ? 'border-foreground bg-muted' : 'border-border hover:bg-muted/50',
              )}
            >
              <span className="block text-sm font-semibold text-foreground">{o.label}</span>
              <span className="mt-0.5 block text-xs text-muted-foreground">{o.help}</span>
            </button>
          ))}
        </div>
        {versions.mode === 'NO_REPEAT' && (
          <label className="flex items-center justify-between gap-3 text-sm">
            <span>
              <span className="font-medium">{t('exam.versions.block_when_exhausted')}</span>
              <span className="block text-xs text-muted-foreground">{t('exam.versions.block_help')}</span>
            </span>
            <Switch
              checked={versions.whenExhausted === 'BLOCK'}
              disabled={locked}
              onChange={(checked) =>
                setDraft((d) => ({ ...d, versions: { ...versions, whenExhausted: checked ? 'BLOCK' : 'REUSE_OLDEST' } }))
              }
            />
          </label>
        )}
        <label className="flex items-center justify-between gap-3 text-sm">
          <span>
            <span className="font-medium">{t('exam.versions.show_code')}</span>
            <span className="block text-xs text-muted-foreground">{t('exam.versions.show_code_help')}</span>
          </span>
          <Switch
            checked={versions.showVersionCode}
            disabled={locked}
            onChange={(checked) => setDraft((d) => ({ ...d, versions: { ...versions, showVersionCode: checked } }))}
          />
        </label>
      </fieldset>

      <Button onClick={save} disabled={locked || saving}>
        {saving ? t('saving') : t('save')}
      </Button>
    </section>
  );
}

export default ExamSettingsCard;
